import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography } from '@mui/material';
import { PacmanLoader } from 'react-spinners';
import { isAuthenticatedByCache } from './Signin/Signin';

function hasAuth0CallbackParams(search) {
  const params = new URLSearchParams(search || '');
  return Boolean(params.get('code') && params.get('state'));
}

function hasAuth0ErrorParams(search) {
  const params = new URLSearchParams(search || '');
  return Boolean(params.get('error'));
}

function buildLoginErrorRedirect(search) {
  const params = new URLSearchParams(search || '');
  const description =
    params.get('error_description') || params.get('error') || '';
  const next = new URLSearchParams();
  if (description) {
    next.set('error', description);
  }
  const query = next.toString();
  return query ? `/login?${query}` : '/login';
}

function AuthCallbackLoader() {
  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '20px',
        backgroundColor: '#262626',
      }}
    >
      <Typography sx={{ color: 'primary.main' }}>Autenticando...</Typography>
      <PacmanLoader color="#ffc356" size={20} />
    </Box>
  );
}

export function PrivateRoute({ component: Component, ...rest }) {
  const { isAuthenticated: auth0Authenticated, isLoading } = useAuth0();

  return (
    <Route
      {...rest}
      render={(props) => {
        const { search } = props.location;
        const cached = isAuthenticatedByCache();
        const isAuthenticated = cached || auth0Authenticated;

        // Erro do Auth0 (ex.: user is blocked) → volta para /login com a mensagem
        if (hasAuth0ErrorParams(search)) {
          return <Redirect to={buildLoginErrorRedirect(search)} />;
        }

        // Callback OAuth em /private — não redirecionar para /login (perdia code/state)
        if (hasAuth0CallbackParams(search) || isLoading) {
          return <AuthCallbackLoader />;
        }

        if (isAuthenticated) {
          return <Component {...props} />;
        }

        return (
          <Redirect
            to={{
              pathname: '/login',
              state: { from: props.location },
            }}
          />
        );
      }}
    />
  );
}

export function PublicRouteLogin({ component: Component, ...rest }) {
  const { isAuthenticated: auth0Authenticated, isLoading } = useAuth0();
  const cached = isAuthenticatedByCache();
  const isAuthenticated = cached || auth0Authenticated;

  return (
    <Route
      {...rest}
      render={(props) => {
        if (isLoading) {
          return <AuthCallbackLoader />;
        }

        if (isAuthenticated) {
          return <Redirect to="/private" />;
        }

        return <Component {...props} />;
      }}
    />
  );
}
